import React from "react";
import LegalPage from "../components/LegalPage";

export default function Privacy() {
  return (
    <LegalPage
      testId="privacy-page"
      eyebrow="Legal"
      title="Privacy Policy"
      lastUpdated="February 2026"
    >
      <p>
        BizPulse AI (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;) respects your privacy. This Privacy
        Policy explains what personal data we collect when you use bizpulseai.net or take an
        Business Growth Discovery™ hosted on our platform, how we use that data, who we share it with, and the rights
        you have.
      </p>

      <h2>1. Who We Are</h2>
      <p>
        BizPulse AI is a lead-generation platform for independent consultants. The data controller for
        this Service is BizPulse AI. For privacy questions, contact{" "}
        <a href="mailto:contact@bizpulseai.net">contact@bizpulseai.net</a>.
      </p>

      <h2>2. Data We Collect</h2>
      <p>We collect:</p>
      <ul>
        <li>
          <strong>Account data</strong> &mdash; name, company, email, password (hashed), profile photo,
          biography, expertise, countries and industries served, WhatsApp number, booking link.
        </li>
        <li>
          <strong>Discovery &amp; lead data</strong> &mdash; country, industry and function chosen, answers
          to Business Growth Discovery™ questions, the resulting score and opportunity analysis, and the contact details
          (name, email, phone, company, position) submitted by business leaders who take the Discovery.
        </li>
        <li>
          <strong>Billing data</strong> &mdash; processed by Paddle (see &ldquo;Processors&rdquo; below); we
          never receive or store full card numbers.
        </li>
        <li>
          <strong>Technical data</strong> &mdash; IP address, browser type, device, pages visited, and
          interaction events used to operate and secure the Service.
        </li>
      </ul>

      <h2>3. How We Use Your Data</h2>
      <ul>
        <li>To provide the Service: generate Business Growth Discoveries™, score responses, deliver reports, and route leads to the right consultant;</li>
        <li>To send transactional emails (account, invoices, lead notifications, password reset);</li>
        <li>To process payments and prevent fraud;</li>
        <li>To improve the Service through aggregated, anonymised analytics;</li>
        <li>To comply with our legal obligations.</li>
      </ul>

      <h2>4. Cookies</h2>
      <p>
        We use a small set of strictly-necessary cookies (authentication, CSRF, view-mode) and may use
        first-party analytics cookies to understand how the Service is used. We do not use third-party
        advertising cookies. You can clear cookies at any time from your browser settings; doing so
        may sign you out.
      </p>

      <h2>5. Third-Party Processors</h2>
      <p>
        We share personal data only with vetted processors who act under our instructions and contractual
        data-protection obligations:
      </p>
      <ul>
        <li>
          <strong>Paddle.com Inc.</strong> (United States / global) &mdash; merchant of record for payments,
          tax collection, refunds, and invoicing.
        </li>
        <li>
          <strong>MongoDB Atlas</strong> (MongoDB, Inc.) &mdash; managed database hosting for all account,
          Discovery, and lead data.
        </li>
        <li>
          <strong>Resend</strong> (Resend Inc.) &mdash; transactional email delivery (account emails, lead
          notifications, PDF report delivery to consultants).
        </li>
        <li>
          <strong>Anthropic</strong> (Anthropic, PBC) &mdash; Claude large-language-model API used to generate
          Business Growth Discovery™ questions and Executive Growth Reports™. Prompts contain Discovery context (country,
          industry, function, answers) but no consultant credentials or billing data.
        </li>
      </ul>

      <h2>6. International Transfers</h2>
      <p>
        Your data may be processed in countries other than your own, including the United States and the
        European Union, depending on where our processors host their infrastructure. Where required, we
        rely on standard contractual clauses or other lawful transfer mechanisms.
      </p>

      <h2>7. Retention</h2>
      <p>
        Account data is retained for as long as your account is active and for up to 24 months after
        closure for legal, tax, and accounting purposes. Lead data is retained for as long as the
        attributed consultant&rsquo;s account remains active. Backups are kept for up to 90 days.
      </p>

      <h2>8. Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you;</li>
        <li>Correct inaccurate or incomplete data;</li>
        <li>Request deletion (right to be forgotten);</li>
        <li>Export your data in a portable format;</li>
        <li>Object to or restrict certain processing;</li>
        <li>Withdraw consent where processing is based on consent;</li>
        <li>Lodge a complaint with your local data-protection authority.</li>
      </ul>
      <p>
        To exercise any of these rights, email{" "}
        <a href="mailto:contact@bizpulseai.net">contact@bizpulseai.net</a> from the email address linked
        to your account.
      </p>

      <h2>9. Security</h2>
      <p>
        We use TLS for data in transit, password hashing (bcrypt) for credentials, HTTPS-only secure
        cookies for authentication, role-based access control internally, and regular dependency and
        vulnerability scans. No system is 100% secure, but we treat security as a continuous priority.
      </p>

      <h2>10. Children</h2>
      <p>
        The Service is not directed to children under 18. We do not knowingly collect personal data
        from children. If you believe a child has provided us with personal data, contact us and we
        will delete it.
      </p>

      <h2>11. Changes</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be notified by email
        or in-app banner. The &ldquo;last updated&rdquo; date at the top reflects the most recent revision.
      </p>

      <h2>12. Contact</h2>
      <p>
        Privacy questions or rights requests:{" "}
        <a href="mailto:contact@bizpulseai.net">contact@bizpulseai.net</a>.
      </p>
    </LegalPage>
  );
}
